import { HgSupportedLanguage } from '@smg-real-estate/domus';
import {
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
  createRouter as _createRouter,
  createWebHistory,
  type RouteLocationNormalizedGeneric,
  type RouteRecordRaw,
  type Router,
} from 'vue-router';

import { checkCustomerAuthorization } from '@/api/agency';
import { setMockUser } from '@/auth/setMockUser';
import { Roles } from '@/components/Agency/UserFormData/types';
import { DEFAULT_LANGUAGE } from '@/i18n';
import { safeLoadTranslations } from '@/i18n-setup';
import { i18n } from '@/i18n-setup-hg';
import LayoutBlank from '@/layouts/LayoutBlank.vue';
import { setAppRouter } from '@/router/appRouter';
import { isUnsupportedBrowser } from '@/router/routerGuardHelpers';
import BusinessCustomerRoutes from '@/router/routes/businessCustomerRoutes';
import { trackAuthenticationAfterRedirect, trackPageLoadEvent } from '@/services/tracking/Tracking';
import store from '@/store';

import auth0 from '@/auth/auth0Plugin';
import {
  buildAuthLoginAuthorizationParams,
  getAgencyHintQueryParam,
} from '@/auth/loginHintQueryParams';
import { parseTargetPath } from '@/auth/parseTargetPath';
import { ModalActionPayload, ModalIds } from '@/components/ModalManager/types';
import { LoadTrackerEventName, initialLoadTracker } from '@/helpers/Monitoring/DataDogMonitor';
import { mapAuth0User } from '@/helpers/authUtils';
import { viewNamePrefix } from '@/router/routerConstants';
import spaScrollBehaviourHandler from '@/router/spaScrollBehaviourHandler';
import { ConversionSource, PageSubType } from '@/services/tracking/GtmTrackingService';
import { useAgencyStore } from '@/stores/agency/useAgencyStore';
import { useUserStore } from '@/stores/user/useUserStore';
import { AuthorizedUser, UserGroup } from '@/types';
import { User } from '@auth0/auth0-vue';
import { captureError } from '@smg-real-estate/browser-rum';
import { setupRumViewTracking } from '@smg-real-estate/browser-rum/vue';
import { unref } from 'vue';
import { isPrivateRoute } from './routerAuth';
import routeNames from './routes/routeNames';

const UNSUPPORTED_BROWSER_PATH = 'unsupported-browser';
const INTELLIGENCE_HUB_ROUTE_NAME = 'intelligence-hub-customer-pool';
const UNSUBSCRIBE_ROUTE_NAME = 'unsubscribe-nba';

const routes: RouteRecordRaw[] = [
  {
    path: '/:locale/ubm/intelligence-hub-customer-pool',
    name: INTELLIGENCE_HUB_ROUTE_NAME,
    component: () => import('@/views/IntelligenceHubLanding/IntelligenceHubLanding.vue'),
    meta: {
      viewName: `${viewNamePrefix} Intelligence Hub Landing Page`,
      gaName: PageSubType.INTELLIGENCE_HUB_LANDING,
      hasBlankLayout: true,
    },
  },
  {
    path: '/:locale/ubm/unsubscribe-nba',
    name: UNSUBSCRIBE_ROUTE_NAME,
    component: () => import('@/views/UnsubscribePage/UnsubscribePage.vue'),
    meta: {
      viewName: `${viewNamePrefix} Unsubscribe NBA Page`,
      hasBlankLayout: true,
    },
  },
  {
    path: '/:locale',
    name: 'main',
    component: LayoutBlank,
    meta: {
      displayMainNavigation: true,
    },
    children: [
      {
        path: 'ubm',
        children: BusinessCustomerRoutes,
        meta: {
          // all the child routes require token, so redirect to login immediately
          authorizedGroups: [UserGroup.AGENCY],
        },
      },
      {
        path: 'ubm/login-callback',
        component: LayoutBlank,
        beforeEnter: async (
          to: RouteLocationNormalized,
          from: RouteLocationNormalizedLoaded,
          next: NavigationGuardNext,
        ) => {
          // If there are any issues with authorization, redirect to `/${locale}/ubm`
          try {
            const appState = await auth0.handleRedirectCallback();
            const targetPath = appState.appState?.target;
            const authenticatedUser = auth0.isAuthenticated.value
              ? mapAuth0User(auth0.user.value)
              : null;

            if (!authenticatedUser) {
              return next(false);
            }

            const agencyHint = targetPath
              ? getAgencyHintQueryParam(parseTargetPath(targetPath).queryParams)
              : undefined;

            await initialize(authenticatedUser, agencyHint);

            trackAuthenticationAfterRedirect(
              authenticatedUser.userId,
              auth0.user.value?.is_first_time_login,
            );

            if (targetPath) {
              const { relativePath, queryParams } = parseTargetPath(targetPath);
              return next({
                path: relativePath,
                query: { ...queryParams },
                replace: true,
              });
            }
            next(false);
          } catch (e) {
            captureError(e);
            // If the user has landed here from a different route in this app, they will have their preferred locale in `targetRoute` and we prioritize it.
            // Otherwise, we default to this path's locale, which at the time of writing this is always 'en'.
            const locale = from.params.locale || to.params.locale;

            next({
              path: `/${locale}/ubm`,
              replace: true,
            });
          }
        },
      },
      {
        path: 'ubm/logout',
        name: routeNames.logout,
        component: LayoutBlank,
        meta: { viewName: `${viewNamePrefix} Logout Page` },
        beforeEnter: async (to: RouteLocationNormalized) => {
          await auth0.logout({
            logoutParams: {
              returnTo: `${window.location.protocol}//${window.location.host}/${to.params.locale}/ubm`,
            },
          });
        },
      },
      {
        path: UNSUPPORTED_BROWSER_PATH,
        name: UNSUPPORTED_BROWSER_PATH,
        component: () => import('@/views/UnsupportedBrowserPage/UnsupportedBrowserPage.vue'),
        meta: {
          authorizedRoles: [Roles.Admin, Roles.Editor],
        },
      },
    ],
  },
  {
    path: '/:locale/:pathMatch(.*)*',
    name: 'pageNotFound',
    component: () => import('@smg-real-estate/domus/HgPageNotFound.vue'),
    props: () => ({
      pageLayoutCssClass: 'layout-container',
    }),
    meta: {
      authorizedRoles: [Roles.Admin, Roles.Editor],
    },
  },
];

const ensureUserCanAccessAgency = async () => {
  const agencyStore = useAgencyStore();
  if (!agencyStore.activeAgencyId) return;

  const authorizationGranted = await checkCustomerAuthorization();
  if (!authorizationGranted) {
    await store.dispatch('openModal', { id: ModalIds.NoActiveAgencyModal } as ModalActionPayload);
  }
};

const initialize = async (
  authenticatedUser: AuthorizedUser,
  agencyHint?: string,
): Promise<void> => {
  const userStore = useUserStore();

  initialLoadTracker.trackEvent(LoadTrackerEventName.userInit);
  // HAS TO BE AWAITED AND COMMITTED TO STORE before continuing
  await userStore.setUserData(authenticatedUser);
  // used for user profile page (in dropdown)
  userStore.setUserDetails(authenticatedUser.auth0Id);

  const agencyStore = useAgencyStore();
  await agencyStore.fetchAgencies();
  await agencyStore.switchToAgency(agencyHint);
  initialLoadTracker.endEvent(LoadTrackerEventName.userInit);
};

export function createRouter(defaultLanguage: HgSupportedLanguage): Router {
  const router = _createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    scrollBehavior: spaScrollBehaviourHandler,
  });

  // unsupported browser redirect
  router.beforeEach((to, from, next) => {
    if (isUnsupportedBrowser() && to.name !== UNSUPPORTED_BROWSER_PATH) {
      const locale = getLocale(to, defaultLanguage);
      next(`/${locale}/${UNSUPPORTED_BROWSER_PATH}`);
    } else {
      next();
    }
  });

  // UI mock auth
  router.beforeEach(async (to, from, next) => {
    await setMockUser(store);

    next();
  });

  // refresh token and re-initialize agency if needed
  router.beforeEach(async (to, from, next) => {
    if (!auth0.isLoading.value) {
      return next();
    }

    // refresh token if PKCE flow hasn't finished
    await auth0.checkSession();

    // skip if not authenticated
    if (!auth0.isAuthenticated.value) {
      return next();
    }

    const authenticatedUser = mapAuth0User(auth0.user.value);
    await initialize(authenticatedUser as AuthorizedUser);

    return next();
  });

  // authentication
  router.beforeEach(async (to, from, next) => {
    if (!isPrivateRoute(to)) {
      return next();
    }

    if (!auth0.isAuthenticated.value) {
      const locale = getLocale(to, defaultLanguage);
      await auth0.loginWithRedirect({
        appState: { target: to.fullPath },
        authorizationParams: buildAuthLoginAuthorizationParams(locale, to.query),
      });
      return next(false);
    }

    // Re-assign targetRoute with additional values to the meta key.
    // These values are available in each component (e.g. `$route.meta?.user`)
    Object.assign(to.meta, { ...to.meta, user: auth0.user.value });

    next();
  });

  // load translations if locale changes
  router.beforeEach(async (to, from, next) => {
    const locale = getLocale(to, defaultLanguage);
    if (!(locale in i18n.global.messages)) {
      await safeLoadTranslations(locale);
    }
    next();
  });

  // blocks user based on role and authorization check
  router.beforeEach(async (to, from, next) => {
    // Skip all agency-related checks for intelligence-hub route
    if (to.name === INTELLIGENCE_HUB_ROUTE_NAME) {
      return next();
    }

    const userStore = useUserStore();
    const hasUserCCCGroup = userStore.hasCCCGroup;
    if (!to?.meta?.authorizedRoles?.length || hasUserCCCGroup) {
      return next();
    }

    // kick out fraudsters and all logins from suspended agencies
    // gets called on every route change
    await ensureUserCanAccessAgency();
    const activeAgency = useAgencyStore().activeAgency;

    // If a user has no active agency, all they should see is their profile page
    if (!activeAgency && to.name !== 'user-profile') {
      await store.dispatch('openModal', { id: ModalIds.NoActiveAgencyModal } as ModalActionPayload);
    }

    if (to?.meta?.authorizedRoles?.includes(activeAgency?.role || Roles.Editor)) {
      return next();
    } else {
      const locale = getLocale(to, defaultLanguage);
      const path = `/${locale}/ubm`;
      if (to.path !== path) return next({ path });
    }
  });

  // Prevent that the load status remains in the wrong status when changing the routing.
  router.afterEach((to, from) => {
    if (to.name !== from.name) {
      store.commit('resetFunnelApiLoading');
    }
  });

  // track every route change in UBM
  router.afterEach((to, _from, _next) => {
    if (to.meta?.gaName) {
      const conversionSource = getConversionSourceFromHistoryState();

      trackPageLoadEvent({
        agencyId: useAgencyStore().activeAgencyId as string,
        language: unref(i18n.global.locale),
        pageSubType: to.meta.gaName,
        userId: useUserStore().userId,
        userTypeLogin: useUserStore().getUserTypeLogin,
        ...(conversionSource ? { conversionSource } : {}),
      });

      if (conversionSource !== undefined) {
        clearNavigationConversionSource();
      }
    }
  });

  router.afterEach(async (to) => {
    // Skip MFA check for intelligence-hub route
    if (to.name === INTELLIGENCE_HUB_ROUTE_NAME) {
      return;
    }
  });

  setupRumViewTracking(router);

  return router;
}

export const router = createRouter(DEFAULT_LANGUAGE);

setAppRouter(router);

function getConversionSourceFromHistoryState(): ConversionSource | undefined {
  const conversionSource = history.state?.conversionSource;

  if (
    typeof conversionSource === 'string' &&
    Object.values(ConversionSource).includes(conversionSource as ConversionSource)
  ) {
    return conversionSource as ConversionSource;
  }

  return undefined;
}

function clearNavigationConversionSource(): void {
  if (history.state?.conversionSource !== undefined) {
    history.replaceState({ ...history.state, conversionSource: undefined }, '');
  }
}

function getLocale(
  route: RouteLocationNormalizedGeneric,
  defaultLanguage: HgSupportedLanguage,
): HgSupportedLanguage {
  return (route.params.locale as HgSupportedLanguage) || route?.meta?.locale || defaultLanguage;
}

declare module 'vue-router' {
  interface RouteMeta {
    authorizedRoles?: Roles[];
    gaName?: PageSubType;
    locale?: HgSupportedLanguage;
    hasBlankLayout?: boolean;
    authorizedGroups?: UserGroup[];
    user?: User;
  }
}
